import React, { useEffect, useState } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Calendar, Lock, Unlock, Trash2, CheckCircle, Shield, Star } from 'lucide-react';
import { useAuth } from '../AuthContext';

const FinancialYears = () => {
  const { isAdminOnly } = useAuth();
  const [financialYears, setFinancialYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [startYear, setStartYear] = useState(new Date().getFullYear());

  useEffect(() => { fetchFYs(); }, []);

  const fetchFYs = async () => {
    try {
      const r = await api.get('/financial-years');
      setFinancialYears(r.data);
    } catch { toast.error('Failed to fetch financial years'); }
    finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/financial-years', { start_year: startYear });
      toast.success(`FY ${startYear}-${String(startYear + 1).slice(-2)} created`);
      setDialogOpen(false);
      fetchFYs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create'); }
  };

  const handleActivate = async (fy) => {
    try {
      await api.put(`/financial-years/${fy.id}/activate`);
      toast.success(`${fy.name} is now active`);
      fetchFYs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to activate'); }
  };

  const handleLock = async (fy) => {
    if (!window.confirm(`Lock ${fy.name}? No further entries can be made for this year.`)) return;
    try {
      await api.put(`/financial-years/${fy.id}/lock`);
      toast.success(`${fy.name} locked (year-end closing)`);
      fetchFYs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to lock'); }
  };

  const handleUnlock = async (fy) => {
    try {
      await api.put(`/financial-years/${fy.id}/unlock`);
      toast.success(`${fy.name} unlocked`);
      fetchFYs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to unlock'); }
  };

  const handleDelete = async (fy) => {
    if (!window.confirm(`Delete ${fy.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/financial-years/${fy.id}`);
      toast.success(`${fy.name} deleted`);
      fetchFYs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  if (!isAdminOnly()) {
    return (
      <div className="p-8 text-center">
        <Shield className="w-16 h-16 mx-auto mb-4 text-red-300" />
        <h2 className="text-xl font-bold text-slate-900 mb-2">Access Denied</h2>
        <p className="text-slate-500">Only Admin can manage financial years.</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="financial-years-page">
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Financial Year Management</h1>
          <p className="text-sm text-slate-600">Manage financial years (April – March). Admin only.</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="create-fy-btn">
              <Plus className="w-4 h-4 mr-2" />Create Financial Year
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Create Financial Year</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label>Start Year (April onwards)</Label>
                <Input
                  type="number"
                  min={2020}
                  max={2035}
                  value={startYear}
                  onChange={e => setStartYear(parseInt(e.target.value))}
                  data-testid="fy-start-year"
                />
                <p className="text-xs text-slate-500">
                  This will create: <b>FY {startYear}-{String(startYear + 1).slice(-2)}</b> (April {startYear} – March {startYear + 1})
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="submit-fy-btn">Create</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div></div>
      ) : financialYears.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
          <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="text-slate-500 mb-2">No financial years created yet</p>
          <p className="text-xs text-slate-400">Click "Create Financial Year" to get started</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {financialYears.map(fy => (
            <div
              key={fy.id}
              className={`bg-white rounded-xl border-2 shadow-sm overflow-hidden transition-all ${
                fy.is_active ? 'border-emerald-500 ring-2 ring-emerald-100' :
                fy.is_locked ? 'border-red-300 bg-red-50/30' : 'border-slate-200'
              }`}
              data-testid={`fy-card-${fy.id}`}
            >
              {/* Header */}
              <div className={`px-5 py-4 ${fy.is_active ? 'bg-emerald-50' : fy.is_locked ? 'bg-red-50' : 'bg-slate-50'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calendar className={`w-5 h-5 ${fy.is_active ? 'text-emerald-600' : fy.is_locked ? 'text-red-500' : 'text-slate-500'}`} />
                    <h3 className="text-lg font-bold text-slate-900">{fy.name}</h3>
                  </div>
                  <div className="flex gap-1">
                    {fy.is_active && (
                      <span className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 font-semibold">
                        <Star className="w-3 h-3" />Active
                      </span>
                    )}
                    {fy.is_locked && (
                      <span className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-red-100 text-red-800 border border-red-300 font-semibold">
                        <Lock className="w-3 h-3" />Locked
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-sm text-slate-600 mt-1">
                  {new Date(fy.start_date + 'T00:00:00').toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })} – {new Date(fy.end_date + 'T00:00:00').toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}
                </p>
              </div>

              {/* Actions */}
              <div className="px-5 py-3 flex flex-wrap gap-2">
                {!fy.is_active && !fy.is_locked && (
                  <Button size="sm" onClick={() => handleActivate(fy)} className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs" data-testid={`activate-fy-${fy.id}`}>
                    <CheckCircle className="w-3.5 h-3.5 mr-1" />Set Active
                  </Button>
                )}
                {!fy.is_locked && (
                  <Button size="sm" variant="outline" onClick={() => handleLock(fy)} className="text-red-700 border-red-200 hover:bg-red-50 h-8 text-xs" data-testid={`lock-fy-${fy.id}`}>
                    <Lock className="w-3.5 h-3.5 mr-1" />Lock (Close Year)
                  </Button>
                )}
                {fy.is_locked && (
                  <Button size="sm" variant="outline" onClick={() => handleUnlock(fy)} className="text-amber-700 border-amber-200 hover:bg-amber-50 h-8 text-xs" data-testid={`unlock-fy-${fy.id}`}>
                    <Unlock className="w-3.5 h-3.5 mr-1" />Unlock
                  </Button>
                )}
                {!fy.is_active && !fy.is_locked && (
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(fy)} className="text-red-600 hover:bg-red-50 h-8 text-xs" data-testid={`delete-fy-${fy.id}`}>
                    <Trash2 className="w-3.5 h-3.5 mr-1" />Delete
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info Section */}
      <div className="mt-8 bg-blue-50 rounded-xl border border-blue-200 p-5">
        <h3 className="text-sm font-bold text-blue-900 mb-2">How Financial Year Management Works</h3>
        <ul className="text-xs text-blue-800 space-y-1.5 list-disc list-inside">
          <li><b>Create:</b> Each FY runs April–March (Indian format). e.g., FY 2025-26 = April 2025 to March 2026.</li>
          <li><b>Set Active:</b> Only one FY can be active at a time. The active FY is shown across the portal.</li>
          <li><b>Lock (Close Year):</b> When a FY is complete, lock it to prevent any further data entry for that period. This is the year-end closing process.</li>
          <li><b>Unlock:</b> If needed, admin can unlock a previously closed FY for corrections.</li>
          <li><b>Delete:</b> Only unlocked and inactive FYs can be deleted.</li>
        </ul>
      </div>
    </div>
  );
};

export default FinancialYears;
